import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Activity, Dna, FlaskConical } from "lucide-react";
import FeatureTabsShowcase from "@/components/FeatureTabsShowcase";
import HowItWorksStepper from "@/components/HowItWorksStepper";
import UseCaseCardStack from "@/components/UseCaseCardStack";
import { useLanguage } from "@/i18n/LanguageProvider";

const HOW_STEPS = [
  {
    title: "Tell Us Your Needs",
    desc: "A few questions on cancer type, US city, and insurance (UnitedHealthcare, Aetna, Medicare, and the rest of our USA list).",
  },
  {
    title: "We Match You Instantly",
    desc: "We filter verified US hospital profiles, NCI-designated centers first, by cancer type and city.",
  },
  {
    title: "Book & Track",
    desc: "Set appointments, upload reports, and follow progress in one dashboard.",
  },
  {
    title: "Get Help When You Need",
    desc: "Second opinions at US NCI centers, prior authorization help, and a coordinator on the phone.",
  },
];

const TEAM_LOGOS = [
  { src: "/logos/meta.svg", alt: "Meta" },
  { src: "https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg", alt: "Amazon" },
  { src: "/logos/microsoft.svg", alt: "Microsoft" },
];

export default function ByOncoPlatformShowcase({ stats }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const rareCount = stats?.rare_cancers;
  const hospitalCount = stats?.hospitals_mapped;

  const serviceItems = [
    {
      id: "hospitals",
      tab: "Find Hospitals",
      title: "Find Hospitals",
      description: "Match NCI-designated US centers by cancer type and city, from Memorial Sloan Kettering in New York City to MD Anderson in Houston.",
      route: "/find-hospitals",
    },
    {
      id: "cost",
      tab: "Cost Calculator",
      title: "Cost Calculator",
      description: "USA cost table in USD for surgery, chemo, radiation, and imaging, so families can plan the next conversation.",
      route: "/cost-calculator",
    },
    {
      id: "rare",
      tab: "Rare Cancers",
      title: "Rare Cancers",
      description: rareCount != null
        ? `Specialized care for ${rareCount} rare and ultra-rare cancer types`
        : "Specialized care for rare and ultra-rare cancer types",
      route: "/rare-cancers",
    },
    {
      id: "opinion",
      tab: "Second Opinion",
      title: "Second Opinion",
      description: "Expert written second opinions with a target turnaround within 12 hours, plus case briefs in minutes.",
      route: "/app",
    },
    {
      id: "tele",
      tab: "Teleconsultation",
      title: "Teleconsultation",
      description: "Secure video with US oncologists from our specialist catalog, at centers such as Memorial Sloan Kettering, St. Jude, and Dana-Farber.",
      route: "/teleconsultation",
    },
    {
      id: "tourism",
      tab: "Medical Tourism",
      title: "Care packages for US oncology",
      description: "Out-of-state NCI care with insurance on file and lodging from our USA table. Built for families traveling for treatment.",
      route: "/journey-builder",
    },
  ];

  const rareUseCases = [
    {
      id: "ultra",
      label: "Ultra-Rare Cancers",
      title: "Ultra-Rare Cancers",
      description: "14 ultra-rare types including DIPG, ATRT, NUT carcinoma. Pediatric tumors, rare carcinomas & aggressive sarcomas.",
      stat: 14,
      statLabel: "types covered",
      icon: <Dna className="w-4 h-4" />,
    },
    {
      id: "very",
      label: "Very Rare Cancers",
      title: "Very Rare Cancers",
      description: "12 high-risk types including Chordoma, Merkel Cell & Angiosarcoma.",
      stat: 12,
      statLabel: "types covered",
      icon: <FlaskConical className="w-4 h-4" />,
    },
    {
      id: "rare",
      label: "Rare Cancers",
      title: "Rare Cancers",
      description: "13 moderately complex types including GIST & Mesothelioma.",
      stat: 13,
      statLabel: "types covered",
      icon: <Activity className="w-4 h-4" />,
    },
  ];
  const shownRareCount =
    typeof rareCount === 'number'
      ? rareCount
      : rareUseCases.reduce((sum, item) => sum + item.stat, 0);

  return (
    <div className="vani-eh">
      <section id="services" className="vani-band">
        <div className="vani-wrap">
          <div className="vani-center vani-reveal">
            <div className="vani-eyebrow">{t.ourServices}</div>
            <h2>{t.servicesH2}</h2>
            <p className="vani-sub">{t.servicesLead}</p>
          </div>
          <div className="vani-eh-stage vani-reveal">
            <FeatureTabsShowcase
              items={serviceItems}
              onCta={(service) => navigate(service.route)}
            />
          </div>
        </div>
      </section>

      <section id="platform">
        <div className="vani-wrap">
          <div className="vani-eh-stage vani-reveal">
            <HowItWorksStepper
              steps={HOW_STEPS}
              eyebrow={t.howByonco}
              heading={t.howByoncoH2}
              lead={t.howByoncoLead}
            />
          </div>
        </div>
      </section>

      <section id="rare-care" className="vani-band">
        <div className="vani-wrap">
          <div className="vani-eh-stage vani-reveal">
            <UseCaseCardStack
              eyebrow={t.specializedCare}
              heading={t.rareUltraH2}
              items={rareUseCases}
            />
          </div>
          <p className="vani-sub vani-center" style={{ marginTop: 28 }}>
            {String(t.rareSpecialize || '').replace('{n}', String(shownRareCount))}
            {hospitalCount != null ? String(t.rareMapped || '').replace('{n}', String(hospitalCount)) : '.'}
          </p>
          <div className="vani-cta-row">
            <Link className="vani-btn vani-btn-primary" to="/rare-cancers">
              {t.viewRare} <span className="disc">→</span>
            </Link>
            <Link className="vani-btn vani-btn-ghost" to="/platform">
              {t.fullMarketplace}
            </Link>
          </div>
        </div>
      </section>

      <section id="team" className="vani-team">
        <div className="vani-wrap vani-center vani-reveal">
          <div className="vani-eyebrow">{t.teamEyebrow}</div>
          <div className="vani-team-logos">
            {TEAM_LOGOS.map((logo) => (
              <div key={logo.alt} className="vani-team-logo">
                <img src={logo.src} alt={logo.alt} />
              </div>
            ))}
          </div>
          <p className="vani-team-tag">
            {t.teamTag}
          </p>
        </div>
      </section>
    </div>
  );
}
